import "./CustomShoutoutPage.css";
import {
  Button,
  Typography,
  Image,
  Card,
  Table,
  Space,
  Modal,
  Select,
  Input,
  Row,
  Col,
  Tooltip,
  Popover,
  Radio,
  message
} from "antd";
import ShoutoutRecommendedRulesModal from "./ShoutoutRecommendedRulesModal";
import { PlusOutlined, NotificationOutlined, ExclamationCircleOutlined, CalendarOutlined } from "@ant-design/icons";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState, useRef, useEffect } from "react";
import {ColorSelector, ColorSelector2} from "./ColorSelector";
import MainContainer from "../Container/MainContainer";
import {
  getBaseUrl,
  doGetRequest,
  doPostRequest
} from "../../helpers/requestHelpers";
import {
  CustomModalFooter
} from "../../helpers/CustomModalFooter"
import {
  toBase64,
  getImageSize,
  getNiceName,
  getImageDimensionsFromUrl,
  isValidHttpUrl
} from "../../helpers/utilityHelpers";

const CUSTOM_SHOUTOUT_TYPE_OPTIONS = {
  STANDARD_CUSTOM_SHOUTOUT: 'STANDARD_CUSTOM_SHOUTOUT',
  FULL_BACKGROUND_CUSTOM_SHOUTOUT: 'FULL_BACKGROUND_CUSTOM_SHOUTOUT'
}
const { Option } = Select;
const { Text, Title } = Typography;

const IMAGE_MUCH_DIFFERENT_TEXT =
  "Your image is much different than the recommended 1:1 dimensions and will appear distorted.";

function App() {
  const [tokenErrorType, setTokenErrorType] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentCustomShoutout, setCurrentCustomShoutout] = useState();
  const [addCustomIconErrorText, setAddCustomIconErrorText] = useState(null);
  const [modalFooterErrorText, setModalFooterErrorText] = useState("")
  const [imageUploadStatusText, setImageUploadStatusText] = useState(null);
  const [cultureOSCalibrationModalOpen, setCultureOSCalibrationModalOpen] = useState(false);
  const [shoutoutRulesModalOpen, setShoutoutRulesModalOpen] = useState(false);
  const [imageDimensions, setImageDimensions] = useState(null);
  const [imageDimensionsLoading, setImageDimensionsLoading] = useState(false);
  const [imageDimensionsError, setImageDimensionsError] = useState(null);

  // show message when tokenErrorType changes
  useEffect(() => {
    // TODO_TRELLO_1136: I'm not sure how we want to handle these errors generally
    if (tokenErrorType) {
      message.error("Error with request. May need to logout and login again.");
    }
  }, [tokenErrorType]);
 
  const [pagination, setPagination] = useState({
    position: ["bottomCenter"],
    pageSize: 50,
    pageSizeOptions: ["10", "20", "50", "100"],
    showSizeChanger: true,
  });

  const inputFile = useRef(null);

  // Networking requests for custom shoutouts
  const getCustomShoutoutsQuery = useQuery(
    ["getCustomShoutouts"],
    async () => {
      return doGetRequest(
        `${getBaseUrl()}/api/getCustomShoutouts`,
        setTokenErrorType
      );
    },
    { refetchOnWindowFocus: false }
  );

  const getCustomShoutoutsQueryRefetch = getCustomShoutoutsQuery.refetch;

  // Add mutation for saving custom shoutout
  const addOrEditCustomShoutoutMutation = useMutation(
    async (jsonToSend) => {
      const response = await doPostRequest(
        `${getBaseUrl()}/api/addOrEditCustomShoutout`,
        jsonToSend,
        setTokenErrorType
      );
      return response;
    }
  );

  // Add useEffect for background image URL validation
  useEffect(() => {
    if (!currentCustomShoutout?.shoutout_background_image_url) {
      setImageDimensions(null);
      setImageDimensionsError(null);
      return;
    }
    setImageDimensionsLoading(true);

    const timer = setTimeout(async () => {
      setImageDimensionsLoading(true);
      setImageDimensionsError(null);
      
      try {
        if(!isValidHttpUrl(currentCustomShoutout.shoutout_background_image_url)){
          setImageDimensionsError('A valid url starting with "http://" or "https://" is required.')
          return
        }
        const dimensions = await getImageDimensionsFromUrl(currentCustomShoutout.shoutout_background_image_url);
        setImageDimensions(dimensions);
        
        // Check if dimensions match recommended size 
        if (dimensions.width !== 1500 || dimensions.height !== 700) {
          setImageDimensionsError(`Image dimensions (${dimensions.width} x ${dimensions.height}) don't match required size (1500 x 700)`);
        }
      } catch (error) {
        setImageDimensionsError("Failed to load image or invalid URL");
        console.error("Error getting image dimensions:", error);
      } finally {
        setImageDimensionsLoading(false);
      }
    }, 500);

    // This is a cleanup function that will either kill the timeout or if the function already ran not do anything
    // This wil effectively function like a debounce
    return () => clearTimeout(timer);
  }, [currentCustomShoutout?.shoutout_background_image_url]);

  async function saveCustomShoutoutClicked() {
    setModalFooterErrorText('');

    if (!currentCustomShoutout.shoutout_name) {
      setModalFooterErrorText("Must provide a Shoutout Name");
      return;
    }

    if(currentCustomShoutout.custom_shoutout_type === CUSTOM_SHOUTOUT_TYPE_OPTIONS.FULL_BACKGROUND_CUSTOM_SHOUTOUT){
      const hasValidImage = currentCustomShoutout.shoutout_background_image_url && imageDimensions && !imageDimensionsLoading && !imageDimensionsError;
      if(!hasValidImage){
        setModalFooterErrorText("A valid and properly sized background image URL is required.");
        return;
      }
    }
    
    const jsonToSend = {
      shoutout_name: currentCustomShoutout.shoutout_name,
      shoutout_description:currentCustomShoutout.shoutout_description,
      custom_shoutout_type:currentCustomShoutout.custom_shoutout_type
    };

    if(currentCustomShoutout.custom_shoutout_type === CUSTOM_SHOUTOUT_TYPE_OPTIONS.STANDARD_CUSTOM_SHOUTOUT){
      jsonToSend.shoutout_color = currentCustomShoutout.shoutout_color;
      if (currentCustomShoutout.shoutout_custom_icon_file) {
        // Adding a file from file system
        const base64Version = await toBase64(
          currentCustomShoutout.shoutout_custom_icon_file
        );
        jsonToSend.shoutout_custom_icon_file = base64Version;
        jsonToSend.shoutout_custom_icon_filename =
          currentCustomShoutout.shoutout_custom_icon_file.name;
        jsonToSend.shoutout_icon = null; // Do not send selected icon in this case
      } else if (currentCustomShoutout.shoutout_custom_icon_path) {
        // Wants to keep using the same custom file path
        jsonToSend.shoutout_custom_icon_path = currentCustomShoutout.shoutout_custom_icon_path;
        jsonToSend.shoutout_icon = null; // Do not send selected icon in this case
      } else {
        jsonToSend.shoutout_icon = currentCustomShoutout.shoutout_icon
      }
    }else if (currentCustomShoutout.custom_shoutout_type === CUSTOM_SHOUTOUT_TYPE_OPTIONS.FULL_BACKGROUND_CUSTOM_SHOUTOUT) {
      jsonToSend.shoutout_background_image_url = currentCustomShoutout.shoutout_background_image_url;
      jsonToSend.shoutout_text_color = currentCustomShoutout.shoutout_text_color;
    }
    
    if (currentCustomShoutout.id) {
      //is an edit
      jsonToSend.id = currentCustomShoutout.id;
    }
    
    await addOrEditCustomShoutoutMutation.mutateAsync(jsonToSend, {
      onSuccess: (response) => { // Got a response from api (including expected errors)
        if(response['success']){
          setIsModalOpen(false);
          getCustomShoutoutsQueryRefetch();
        }else{
          // Assume for this api that 'success' false response come with 'error_text'
          setModalFooterErrorText(`Error saving shoutout: ${response['error_text']}`)
        }
      },
      onError: (error) => { // Unexpected errors
        setModalFooterErrorText("Error saving shoutout")
        // Bugsnag.notify(error); Feel like these errors will appear backend so maybe not needed to snag here?
        console.error(`Error saving custom shoutout: ${error}`);
      }
    });
  }

  async function deleteShoutoutClicked(shoutoutToDelete) {
    const response = await doPostRequest(
      `${getBaseUrl()}/api/deleteCustomShoutout`,
      { custom_shoutout_id: shoutoutToDelete.id },
      setTokenErrorType
    );
    if(response["error"]) {
      window.alert(response["error"]);
    } else {
      getCustomShoutoutsQueryRefetch();
    }
  }

  // Form handlers
  const handleCancel = () => {
    setIsModalOpen(false);
  };

  // handlers
  const handleExplainerModalCancel = () => {
    setIsModalOpen(false);
    setCultureOSCalibrationModalOpen(false);
  };

  const openEditModalWithShoutout = (customShoutout) => {
    setModalFooterErrorText('');
    setAddCustomIconErrorText(null);
    setImageUploadStatusText(null);
    const defaultShoutoutTextColor = '#FFFFFF';
    if (customShoutout) {
      setCurrentCustomShoutout({
        id: customShoutout.id,
        shoutout_name: customShoutout.shoutout_name,
        shoutout_description:customShoutout.shoutout_description,
        shoutout_color: customShoutout.shoutout_color, // ColorSelector component has a default so will bank on that to always have a value here
        shoutout_icon: customShoutout.shoutout_icon || "welcome", // Default here as well so dropdown always has something even if they are editing a custom
        shoutout_custom_icon_file: null,
        shoutout_custom_icon_path: customShoutout.shoutout_custom_icon_path,
        custom_shoutout_type: customShoutout.custom_shoutout_type,
        shoutout_background_image_url: customShoutout.shoutout_background_image_url,
        shoutout_text_color: customShoutout.shoutout_text_color || defaultShoutoutTextColor // Default here as well since we clear this depending on custom_shoutout_type
      });
    } else {
      setCurrentCustomShoutout({
        shoutout_name: "",
        shoutout_description:"",
        shoutout_color: "#FCBC54",
        shoutout_icon: "welcome",
        shoutout_custom_icon_file: null,
        shoutout_custom_icon_path: null,
        custom_shoutout_type: CUSTOM_SHOUTOUT_TYPE_OPTIONS.STANDARD_CUSTOM_SHOUTOUT,
        shoutout_background_image_url:null,
        shoutout_text_color: defaultShoutoutTextColor
      }); /*if changing shoutout_color make sure it is one in ColorSelector and ColorSelector2 components*/
    }
    setIsModalOpen(true);
  };

  const customShoutoutIconChanged = (newColor) => {
    const clonedShoutout = Object.assign({}, currentCustomShoutout);
    clonedShoutout.shoutout_icon = newColor;
    setCurrentCustomShoutout(clonedShoutout);
  };

  const customShoutoutNameChanged = (newNameEvent) => {
    const clonedShoutout = Object.assign({}, currentCustomShoutout);
    clonedShoutout.shoutout_name = newNameEvent.target.value;
    setCurrentCustomShoutout(clonedShoutout);
  };

  const customShoutoutDescriptionChanged = (newDescriptionEvent) => {
    const clonedShoutout = Object.assign({}, currentCustomShoutout);
    clonedShoutout.shoutout_description = newDescriptionEvent.target.value;
    setCurrentCustomShoutout(clonedShoutout);
  };

  const customShoutoutBackgroundImageChanged = (newDescriptionEvent) => {
    const clonedShoutout = Object.assign({}, currentCustomShoutout);
    clonedShoutout.shoutout_background_image_url = newDescriptionEvent.target.value;
    setCurrentCustomShoutout(clonedShoutout);
  };

  const customShoutoutTypeChanged = (newDescriptionEvent) => {
    const clonedShoutout = Object.assign({}, currentCustomShoutout);
    clonedShoutout.custom_shoutout_type = newDescriptionEvent.target.value;
    setCurrentCustomShoutout(clonedShoutout);
  };

  const customShoutoutColorChanged = (newColor) => {
    const clonedShoutout = Object.assign({}, currentCustomShoutout);
    clonedShoutout.shoutout_color = newColor;
    setCurrentCustomShoutout(clonedShoutout);
  };

  // This one takes a color rather than an event
  const customShoutoutTextColorChanged = (newColor) => {
    const clonedShoutout = Object.assign({}, currentCustomShoutout);
    clonedShoutout.shoutout_text_color = newColor;
    setCurrentCustomShoutout(clonedShoutout);
  };

  const onButtonClick = () => {
    // `current` points to the mounted file input element
    inputFile.current.click();
  };

  const onCustomIconAdded = async (evt) => {
    setAddCustomIconErrorText(null);
    setImageUploadStatusText(null);
    const selectedFile = evt.target.files[0];
    const maxAllowedSize = 10 * 1024 * 1024; //10MB limit

    const clonedShoutout = Object.assign({}, currentCustomShoutout);
    clonedShoutout.shoutout_custom_icon_file = null; //Clear the current file always, on success and errors
    if (selectedFile.size > maxAllowedSize) {
      setAddCustomIconErrorText(
        "Image was over 10MB, please select a smaller image and try again."
      );
    } else {
      const imageUrl = URL.createObjectURL(selectedFile);
      const dimensions = await getImageSize(imageUrl);
      const ratio =
        Math.max(dimensions.height, dimensions.width) /
        Math.min(dimensions.height, dimensions.width);
      clonedShoutout.shoutout_custom_icon_file = selectedFile;

      const imageDimensionsText = `Your image is ${dimensions.width} by ${dimensions.height}.`;
      if (ratio > 1.6) {
        // Give a more hardcore warning
        setImageUploadStatusText(
          <Text type="danger">{`${IMAGE_MUCH_DIFFERENT_TEXT} ${imageDimensionsText}`}</Text>
        );
      } else if (ratio > 1) {
        // Show less hardcore warning
        setImageUploadStatusText(
          <Text
            style={{ color: "#003eb3" }}
          >{`Your image does not match the recommended 1:1 dimensions and may appear somewhat distorted. ${imageDimensionsText}`}</Text>
        );
      }
    }
    setCurrentCustomShoutout(clonedShoutout);
  };

  const currentShoutoutHasALocalOrWebCustomIcon = () => {
    return (
      !!currentCustomShoutout.shoutout_custom_icon_file ||
      !!currentCustomShoutout.shoutout_custom_icon_path
    );
  };

  const removeCustomAddedIcon = () => {
    const clonedShoutout = Object.assign({}, currentCustomShoutout);
    // Clear everything related to custom icons (local or url)
    clonedShoutout.shoutout_custom_icon_file = null;
    clonedShoutout.shoutout_custom_icon_path = null;
    setCurrentCustomShoutout(clonedShoutout);
  };

  const getFullUrlForShoutoutIcon = (shoutout) => {
    if (shoutout.shoutout_custom_icon_file) {
      //local image which has not been saved yet
      return URL.createObjectURL(shoutout.shoutout_custom_icon_file);
    } else if (shoutout.shoutout_custom_icon_path) {
      return `${getBaseUrl()}/api/${shoutout.shoutout_custom_icon_path}`;
    } else {
      return `${getBaseUrl()}/api/images/shoutoutCustomIcons/${
        shoutout.shoutout_icon
      }.png`;
    }
  };

  const getPreviewIconImageForShoutout = (shoutout) => {
    return (
      <Image
        style={{
          marginRight: "30px",
          backgroundColor: shoutout.shoutout_color,
        }}
        preview={false}
        width={35}
        src={getFullUrlForShoutoutIcon(shoutout)}
      />
    );
  };

  let shoutoutTypeArea = <></>;
  if(currentCustomShoutout && currentCustomShoutout.custom_shoutout_type === CUSTOM_SHOUTOUT_TYPE_OPTIONS.STANDARD_CUSTOM_SHOUTOUT) {
    shoutoutTypeArea = <>
      <Title level={5}>Choose Icon:</Title>
      <Row gutter={16}>
        <Col className="centered-col">
          <Select
            disabled={currentShoutoutHasALocalOrWebCustomIcon()}
            style={{ width: 175 }}
            value={currentCustomShoutout.shoutout_icon}
            onChange={customShoutoutIconChanged}
          >
            {getCustomShoutoutsQuery.data.shoutoutCustomIconNames.map(
              (custom_shoutout_icon_name) => (
                <Option
                  value={custom_shoutout_icon_name}
                  key={custom_shoutout_icon_name}
                >
                  {getNiceName(custom_shoutout_icon_name)}
                </Option>
              )
            )}
          </Select>
        </Col>
        <div style={{ marginTop: "20px" }}></div>
        <Col className="centered-col">
          {getPreviewIconImageForShoutout(currentCustomShoutout)}
        </Col>
      </Row>
      <div style={{ marginTop: "20px" }}>
        <input
          onChange={onCustomIconAdded}
          type="file"
          id="file"
          ref={inputFile}
          style={{ display: "none" }}
          accept="image/png, image/jpeg"
          onClick={function (evt) {
            // Clear value so onchange gets triggered each time even if same file is selected back to back
            evt.target.value = null;
          }}
        />
        {addCustomIconErrorText && (
          <Text type="danger">{addCustomIconErrorText}</Text>
        )}
        {currentShoutoutHasALocalOrWebCustomIcon() ? (
          <div>
            {getPreviewIconImageForShoutout(currentCustomShoutout)}

            <Button
              onClick={() => removeCustomAddedIcon()}
              type="text"
              danger
            >
              Remove
            </Button>
          </div>
        ) : (
          <Button onClick={onButtonClick}>Upload icon instead</Button>
        )}
        <div style={{ marginTop: "5px" }}>
          {currentShoutoutHasALocalOrWebCustomIcon() ? (
            imageUploadStatusText
          ) : (
            <Text type="secondary">
              Recommended upload dimensions of 1:1
            </Text>
          )}
        </div>
      </div>
      <div style={{ marginTop: "20px" }}></div>
      <Title level={5}>Select Color:</Title>
      <ColorSelector
        colorUpdated={customShoutoutColorChanged}
        currentColor={currentCustomShoutout.shoutout_color}
      />
      <div style={{ marginTop: "40px" }}></div>
    </>
  }else if (currentCustomShoutout && currentCustomShoutout.custom_shoutout_type === CUSTOM_SHOUTOUT_TYPE_OPTIONS.FULL_BACKGROUND_CUSTOM_SHOUTOUT){
    shoutoutTypeArea = (
      <>
        <Title level={5}>Background Image URL:</Title>
        <span style={{ fontSize: '11px', color: 'gray', display: 'block', paddingBottom: '10px', marginTop: '-5px' }}>
          This is the image used as the background of the shoutout (use our
          <Button type="link" href="/app/graphics_library" target="_blank" style={{ fontSize: '11px', padding: 0, height: 'auto' }}> graphics library&nbsp;</Button>
          to upload your own). Graphics must be 1500px width by 700px height. <span>Review our <Button type="link" onClick={() => {setShoutoutRulesModalOpen(true)}} style={{ padding: 0, height: 'auto', fontSize: '11px' }}> recommendations.</Button></span>
        </span>
        <Input
          style={{ width: '100%', fontSize: '12px' }}
          value={currentCustomShoutout.shoutout_background_image_url}
          onChange={customShoutoutBackgroundImageChanged}
          placeholder="URL of background image"
          status={imageDimensionsError ? "error" : imageDimensions ? "success" : ""}
        />
        {imageDimensionsLoading && <div style={{ marginTop: '5px' }}>Checking image dimensions...</div>}
        {!imageDimensionsLoading && imageDimensionsError && <div style={{ marginTop: '5px', color: 'red' }}>{imageDimensionsError}</div>}
        {!imageDimensionsLoading && imageDimensions && !imageDimensionsError && (
          <div style={{ marginTop: '5px', color: 'green' }}>
            ✅ Image dimensions: {imageDimensions.width}x{imageDimensions.height}
          </div>
        )}
        <div style={{ marginTop: "10px" }}></div>
        <Title level={5}>Text Color:</Title>
        <span style={{ fontSize: '11px', color: 'gray', display: 'block', marginTop: '-5px', marginBottom: '10px' }}>
          Color used for text on shoutout (ie. shoutout message itself and text for points rewarded)
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Popover
            content={
              <ColorSelector2
                colorUpdated={customShoutoutTextColorChanged}
                currentColor={currentCustomShoutout.shoutout_text_color}
              />
            }
            trigger="click"
            placement="top"
          >
            <Button>
              Select Color
            </Button>
          </Popover>
          <div>
            Current:
          </div>
          <div
            style={{
              width: "25px",
              height: "25px",
              backgroundColor: currentCustomShoutout.shoutout_text_color,
              borderRadius: "4px",
              flexShrink: 0,
              border: "1px solid #d9d9d9"
            }}
          />
        </div>
        
      </>
    )
  }

  const customFooter = <CustomModalFooter 
    errorText={modalFooterErrorText}
    handleCancel={handleCancel}
    handleSave={saveCustomShoutoutClicked}
    saveButtonIsLoading={addOrEditCustomShoutoutMutation.isLoading}
  />

  // Modal to create shoutout
  const createShoutoutModal = (
    <Modal
      title="Custom Shoutout"
      okText="Save"
      open={isModalOpen}
      onCancel={handleCancel} //Even though using custom footer still need this one for the top right "x"
      centered
      width="550px"
      footer={customFooter}
      bodyStyle={{ maxHeight: 'calc(90vh - 110px)', overflow: 'auto' }}
    >
      <ShoutoutRecommendedRulesModal 
        isOpen={shoutoutRulesModalOpen} 
        onCloseCallback={() => {
          setShoutoutRulesModalOpen(false);
        }}
      />
      {currentCustomShoutout ? (
        <>
          <Title level={5}>Enter Name:</Title>
          <Input 
            style={{ width: 200 }}
            value={currentCustomShoutout.shoutout_name}
            onChange={customShoutoutNameChanged}
            placeholder="Shoutout name"
            maxLength={75} // to satisfy slack limit on dropdown length, error from slack: "must be less than 76 characters"
          />
          <div style={{ marginTop: "20px" }}></div>
          <Title level={5}>Enter Description:</Title>
          <Input
            style={{ width: '100%' }}
            value={currentCustomShoutout.shoutout_description}
            onChange={customShoutoutDescriptionChanged}
            placeholder="Describe when to use this shoutout type"
          />
          <div style={{ marginTop: "20px" }}></div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Title style={{marginBottom: "0px"}} level={5}>Customization Type:</Title>
            <Radio.Group value={currentCustomShoutout.custom_shoutout_type} onChange={customShoutoutTypeChanged} buttonStyle="solid">
              <Radio.Button className="no-focus-style" value={CUSTOM_SHOUTOUT_TYPE_OPTIONS.STANDARD_CUSTOM_SHOUTOUT}>Standard</Radio.Button>
              <Radio.Button className="no-focus-style" value={CUSTOM_SHOUTOUT_TYPE_OPTIONS.FULL_BACKGROUND_CUSTOM_SHOUTOUT}>Full Background</Radio.Button>
            </Radio.Group>
          </div>
          <div style={{ marginTop: "20px" }}></div>
          {shoutoutTypeArea}
        </>
      ) : (
        <></>
      )}
    </Modal>
  );

   // explain results modal
   const cultureOSCalibrationModal = (
    <Modal
      title="CultureOS™ Calibration: Fully Charged"
      open={cultureOSCalibrationModalOpen}
      onCancel={handleExplainerModalCancel}
      centered
      bodyStyle={{ maxHeight: 'calc(90vh - 110px)', overflow: 'auto' }}
      footer={[
        <Button key="back" onClick={handleExplainerModalCancel}>
          Close
        </Button>,
      ]}
    >
      Get a full-spectrum view of your culture—beyond the pulse, into the patterns. This workshop-powered upgrade combines survey data with live facilitation to help you map, align, and activate your culture with intention.

      <br /><br />

      <img width={"100%"} alt="" src="https://graphics.getculturebot.com/other/culture-os-calibration.png" />

      <br /><br />

      ✔️ Full diagnostic survey + employee interviews
      <br />
      ✔️ Live Culture Mapping session (symbols, behaviors, systems)
      <br />
      ✔️ Custom CultureBot playbook + activation roadmap
      <br />
      ✔️ Optional Core Values Development workshop

      <br /><br />

      Perfect for teams navigating growth, drift, or change. Run it once or revisit quarterly for ongoing calibration.

      <br /><br />

      <Button
        type="primary"
        shape="round"
        icon={<CalendarOutlined />}
        onClick={() => window.open("https://calendly.com/culture-bot/30-min-intro-clone", "_blank")}
      >
        Book a CultureOS™ Calibration Session
      </Button>

    </Modal>
  );

  // Main UI
  const columns = [
    {
      title: "Shoutout name",
      dataIndex: "name",
      key: "name",
      render: (_, record) => {
        return <div>{record.shoutout_name}</div>;
      },
    },
    {
      title: "Shoutout Description",
      render: (_, record) => {
        return (
          <Tooltip title={record.shoutout_description} mouseEnterDelay={0.5}>
            <div style={{maxWidth: "300px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
              {record.shoutout_description}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Shoutout Style",
      render: (_, record) => {
        if(record.custom_shoutout_type === CUSTOM_SHOUTOUT_TYPE_OPTIONS.STANDARD_CUSTOM_SHOUTOUT){
          return (
            <div style={{ width: "140px", display: "flex", alignItems: "center", gap: "8px" }}>
              <div
                style={{
                  width: "30px",
                  height: "30px",
                  backgroundColor: record.shoutout_color,
                  borderRadius: "4px",
                  flexShrink: 0
                }}
              />
              <div>
                with icon
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                {getPreviewIconImageForShoutout(record)}
              </div>
            </div>
          );
        }else if(record.custom_shoutout_type === CUSTOM_SHOUTOUT_TYPE_OPTIONS.FULL_BACKGROUND_CUSTOM_SHOUTOUT){
          return <div style={{ width: "140px", display: "flex", alignItems: "center", gap: "8px" }}>
            <Popover 
              content={
                <div style={{ width: 300, height: 150, overflow: "hidden" }}>
                  <Image
                    preview={false}
                    width="100%"
                    height="100%"
                    style={{ objectFit: "contain" }}
                    src={record.shoutout_background_image_url}
                  />
                </div>
              }
              trigger="hover"
              mouseEnterDelay={0.2}
            >
              <Image
                preview={false}
                height={40}
                src={record.shoutout_background_image_url}
              />
            </Popover>
          </div>
        } else {
          return <></>
        }
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" onClick={() => openEditModalWithShoutout(record)}>Edit Shoutout</Button>
          <Button type="link" onClick={() => deleteShoutoutClicked(record)}>Delete Shoutout</Button>
        </Space>
      ),
    },
  ];

  // Table Data
  var tableArea;
  if (getCustomShoutoutsQuery.isLoading) {
    tableArea = <div>Loading custom shoutouts...</div>;
  } else if (getCustomShoutoutsQuery.isError || getCustomShoutoutsQuery.data.error) {
    tableArea =
    "Something went wrong, please refresh and try again. If the problem persists please contact support@getculturebot.com";
  } else {
    const tableData = getCustomShoutoutsQuery.data.customShoutouts.map(
      (currentShoutout) => {
        return {
          id: currentShoutout.id,
          key: currentShoutout.id,
          shoutout_name: currentShoutout.shoutout_name,
          shoutout_description: currentShoutout.shoutout_description,
          shoutout_color: currentShoutout.shoutout_color,
          shoutout_icon: currentShoutout.shoutout_icon,
          shoutout_custom_icon_path: currentShoutout.shoutout_custom_icon_path,
          custom_shoutout_type: currentShoutout.custom_shoutout_type,
          shoutout_background_image_url: currentShoutout.shoutout_background_image_url,
          shoutout_text_color: currentShoutout.shoutout_text_color
        };
      }
    );
    tableArea = (
      <Table
        columns={columns}
        dataSource={tableData}
        pagination={{
          ...pagination,
          onChange: (page, pageSize) => {
            setPagination((prev) => ({ ...prev, pageSize }));
          },
        }}
        scroll={{ x: 'max-content' }} // Allows horizontal scrolling if needed
      />
    );
  }

  // Page Frame
  let mainArea;
  mainArea = (
    <>
      <Title>
        <NotificationOutlined style={{ fontSize: "32px" }} /> Custom Shoutouts
      </Title>

      {/* top left side */}
      <Button
        type="link"
        onClick={() => {
          setCultureOSCalibrationModalOpen(true);
        }}
        className="explainResults"
        style={{ padding: 0, height: 'auto' }}
      >
        <ExclamationCircleOutlined />  Not sure how to define your culture? We'll guide you with CultureOS™ Calibration.
        
      </Button>
      <br />
      <br />

      <Button
        type="primary"
        shape="round"
        icon={<PlusOutlined />}
        onClick={() => openEditModalWithShoutout()}
      >
        Add custom shoutout
      </Button>

      <div style={{ marginTop: "30px" }}></div>
      {tableArea}
    </>
  );

  return (
    <>
      <MainContainer>
        <div className="main-container">
          {createShoutoutModal}
          {cultureOSCalibrationModal}
          <Card className="main-container-card">{mainArea}</Card>
        </div>
      </MainContainer>
    </>
  );
}

export default App;
